import { MONITOR_KEY_SECRET } from 'Config/proctoring.config'

export type MonitorKeyPayload = {
    tenant: string
    profile: string
    duration: number
    reference_id: number
}

const toBase64Bytes = (encoded: string) => {
    const normalized = encoded.replaceAll(' ', '+')
    const raw = atob(normalized)
    return Uint8Array.from(raw, char => char.codePointAt(0) ?? 0)
}

let cachedDecryptKey: Promise<CryptoKey> | null = null

const getDecryptKey = () => {
    if (!cachedDecryptKey) {
        const secret = MONITOR_KEY_SECRET.padEnd(32).slice(0, 32)
        cachedDecryptKey = crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(secret),
            { name: 'AES-GCM' },
            false,
            ['decrypt']
        )
    }
    return cachedDecryptKey
}

/** Decrypt Talent Studio `pc` payload (AES-GCM, base64(iv+ciphertext)). */
export const decryptMonitorKey = async (encrypted: string): Promise<MonitorKeyPayload> => {
    const combined = toBase64Bytes(encrypted)
    const iv = combined.slice(0, 12)
    const ciphertext = combined.slice(12)
    const keyMaterial = await getDecryptKey()
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, keyMaterial, ciphertext)
    return JSON.parse(new TextDecoder().decode(decrypted))
}

/**
 * Validates Talent Studio monitor payload.
 * Returns null when valid; otherwise an error message.
 */
export const validateMonitorKeyPayload = (payload: unknown) => {
    if (!payload || typeof payload !== 'object') {
        return 'Invalid monitor key payload'
    }
    const { tenant, profile, duration, reference_id } = payload as Partial<MonitorKeyPayload>
    if (typeof tenant !== 'string' || !tenant.trim()) {
        return 'Monitor key is missing a valid tenant'
    }
    if (typeof profile !== 'string' || !profile.trim()) {
        return 'Monitor key is missing a valid profile'
    }
    if (typeof duration !== 'number' || !Number.isFinite(duration) || duration <= 0) {
        return 'Monitor key is missing a valid duration (seconds)'
    }
    if (typeof reference_id !== 'number' || !Number.isFinite(reference_id)) {
        return 'Monitor key is missing a valid reference_id'
    }
    return null
}
